<script setup>
import ImportTrackedKeywordsAction from '@frontJs/components/actions/projectManagement/seo/ImportTrackedKeywordsAction.vue';
import UpdateTrackedKeywordAction from '@frontJs/components/actions/projectManagement/seo/UpdateTrackedKeywordAction.vue';
import TrackedKeywordImportantToFollowCell from '@frontJs/components/tables/cells/projectManagement/seo/TrackedKeywordImportantToFollowCell.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import TrashIcon from '@iconify-vue/ph/trash';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastDeleted } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, countries } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'keyword',
            header: () => 'Mot-clé',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.keyword,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isTracked',
            header: () => 'Suivi actif',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isTracked,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'importantToFollow',
            header: () => 'Important',
            cell: (props) =>
                h(TrackedKeywordImportantToFollowCell, {
                    tenant,
                    seoTrackedKeyword: props.row.original,
                    callback: updateData,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'country',
            header: () => 'Pays',
            cell: (props) =>
                props.row.original.country
                    ? h(PillCell, {
                          pillValue: props.row.original.country.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'language',
            header: () => 'Langue',
            cell: (props) =>
                props.row.original.language
                    ? h(PillCell, {
                          pillValue: props.row.original.language.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'targets',
            header: () => 'Cibles',
            cell: (props) => {
                const targets = props.row.original.targets;
                const targetCount = targets.reduce(
                    (count, variant) => count + variant.googleWebsites.length,
                    0,
                );

                return h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DisplayContentAction,
                                { title: 'Cibles' },
                                {
                                    trigger: () =>
                                        h(
                                            'span',
                                            {
                                                class: 'cursor-pointer text-blue-500',
                                            },
                                            `${targetCount} cible(s)`,
                                        ),
                                    complexContent: () =>
                                        h(
                                            'ul',
                                            { class: 'mt-2 list-disc pl-8' },
                                            targets.map((variantTargets) =>
                                                h(
                                                    'li',
                                                    {
                                                        key: variantTargets.variantId,
                                                    },
                                                    [
                                                        `Site "${variantTargets.variantName}"`,
                                                        h(
                                                            'ul',
                                                            {
                                                                class: 'list-disc pl-8',
                                                            },
                                                            variantTargets.googleWebsites.map(
                                                                (
                                                                    googleWebsite,
                                                                ) =>
                                                                    h(
                                                                        'li',
                                                                        {
                                                                            key: googleWebsite.id,
                                                                        },
                                                                        googleWebsite.name,
                                                                    ),
                                                            ),
                                                        ),
                                                    ],
                                                ),
                                            ),
                                        ),
                                },
                            ),
                    },
                );
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'searchVolume',
            header: () => 'Volume',
            headerAlign: 'right',
            cell: (props) =>
                props.row.original.searchVolume
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.searchVolume,
                              align: 'right',
                          },
                          props.row.original.searchVolumeChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .searchVolumeChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Volume de recherche',
                                                    label: 'Volume de recherche',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .searchVolumeChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .searchVolumeChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Volumes de recherche',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastRefreshedAt',
            header: () => 'Dernière MAJ',
            cell: (props) =>
                props.row.original.lastRefreshAt
                    ? h(TextCell, {
                          text: props.row.original.lastRefreshAt,
                      })
                    : undefined,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                UpdateTrackedKeywordAction,
                                {
                                    tenant,
                                    countries,
                                    seoTrackedKeyword: props.row.original,
                                    callback: updateData,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            icon: PencilSimpleIcon,
                                            size: 'small',
                                            square: true,
                                            title: 'Modifier',
                                            type: 'primary',
                                        }),
                                },
                            ),
                            h(
                                DeleteAction,
                                {
                                    url: route(
                                        'front.projectManagement.seo.deleteTrackedKeyword',
                                        {
                                            tenant: tenant.id,
                                            seoTrackedKeyword:
                                                props.row.original.id,
                                        },
                                    ),
                                    callback: () => {
                                        toastDeleted();
                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            type: 'secondary',
                                            icon: TrashIcon,
                                            size: 'small',
                                            square: true,
                                            title: 'Supprimer',
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postTrackedKeywords', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ImportTrackedKeywordsAction
                :tenant="tenant"
                :callback="updateData"
            >
                <template #button="{ setIsOpen }">
                    <ActionButton
                        size="small"
                        :callback="() => setIsOpen(true)"
                        :left-icon="UploadSimpleIcon"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportTrackedKeywordsAction>
        </template>
    </DataTableLayout>
</template>
